import Swal from "sweetalert2";
import trustBadgeUrl from "../assets/report_custody_trust_badge.png";

// ---------------------------------------------------------------------------
// PLACEHOLDER NOTICE - wording and version are pending legal / DPO sign-off.
// Bump NOTICE_VERSION whenever any text below changes: it is stored with every
// acknowledgement so you can prove which wording the user agreed to.
// ---------------------------------------------------------------------------
export const NOTICE_VERSION = "TS-EXPORT-v1.0-DRAFT";

const NOTICE = {
  title: "Protected Assessment Export",
  assent:
    "I agree to safeguard the personal data in this report, keep TalentSage IP proprietary, " +
    "and not upload this file into public AI training tools.",
  cancel: "Cancel",
  confirm: { pdf_download: "Download PDF", pdf_view: "Open PDF", print: "Print" },
  preparing: { pdf_download: "Preparing Secure PDF…", pdf_view: "Preparing Secure PDF…", print: "Preparing to print…" },
  error: "We couldn't record your agreement. Please try again.",
};

const esc = (s) =>
  String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

// Scoped styles, injected once. Uses the app's CSS variables with safe fallbacks.
function ensureStyles() {
  if (document.getElementById("rc-modal-style")) return;
  const style = document.createElement("style");
  style.id = "rc-modal-style";
  style.textContent = `
.swal2-popup.rc-popup{width:min(92vw,440px);padding:0;overflow:hidden;border-radius:var(--radius-lg,.75rem);font-family:var(--font-family,'Inter',sans-serif)}
.rc-head{display:flex;justify-content:center;padding:14px 16px 4px;background:#fef7e5}
.rc-head img{display:block;height:104px;width:auto;max-width:100%}
.swal2-html-container.rc-body{margin:0;padding:18px 24px 0;text-align:left;overflow:visible}
.rc-title{margin:0;text-align:center;font-size:18px;font-weight:600;line-height:1.3;color:var(--primary,#002955)}
.rc-sub{margin:4px 0 0;text-align:center;font-size:12px;color:var(--outline,#737781)}
.rc-assent{display:flex;align-items:flex-start;gap:12px;margin-top:18px;padding:14px;cursor:pointer;
  border:1px solid var(--card-border,#e2e8f0);border-radius:8px;background:var(--surface-container-low,#eff4ff);
  font-size:13px;line-height:1.55;color:var(--on-surface,#0b1c30);user-select:none}
.rc-assent input{flex:none;width:16px;height:16px;margin:3px 0 0;accent-color:var(--primary,#002955);cursor:pointer}
.rc-assent:focus-within{outline:2px solid var(--primary,#002955);outline-offset:2px}
.swal2-actions.rc-actions{width:100%;box-sizing:border-box;margin:20px 0 0;padding:0 24px 22px;justify-content:flex-end;gap:10px}
.rc-cancel,.rc-confirm{font:600 13px var(--font-family,'Inter',sans-serif);border-radius:6px;padding:9px 16px;cursor:pointer;border:0}
.rc-cancel{background:transparent;color:var(--on-surface-variant,#434750)}
.rc-cancel:hover:not(:disabled){background:var(--surface-container-low,#eff4ff)}
.rc-confirm{display:inline-flex;align-items:center;gap:8px;background:var(--primary,#002955);color:var(--on-primary,#fff)}
.rc-confirm:disabled,.rc-cancel:disabled{opacity:.5;pointer-events:none}
.rc-cancel:focus-visible,.rc-confirm:focus-visible{outline:2px solid var(--primary,#002955);outline-offset:2px}
.swal2-popup.rc-popup .swal2-validation-message{margin:14px 0 0;padding:10px 24px;font-size:13px;justify-content:flex-start}
.rc-spin{width:14px;height:14px;border:2px solid currentColor;border-right-color:transparent;border-radius:50%;animation:rc-spin .7s linear infinite}
@keyframes rc-spin{to{transform:rotate(360deg)}}
@media (prefers-reduced-motion:reduce){.rc-spin{animation-duration:2s}}
`;
  document.head.appendChild(style);
}

/**
 * Shows the "Protected Assessment Export" checkpoint.
 *  - checkbox is focused on open; the main button stays disabled until it is ticked
 *  - Space ticks, Enter then confirms; Esc / click outside / Cancel dismiss (nothing is released)
 *  - on confirm the server records the agreement (identity from the session) while the button
 *    shows "Preparing Secure PDF…"; the dialog closes when the record exists
 * Resolves to { ack_id, accepted_at, viewer_name, access_capacity }, or null if dismissed.
 */
export async function requestAcknowledgement({ api, surveyAssignmentId, reportTemplateId, action, reportTitle }) {
  ensureStyles();
  const act = NOTICE.confirm[action] ? action : "pdf_download";
  let busy = false;

  const result = await Swal.fire({
    html:
      `<div class="rc-head"><img src="${trustBadgeUrl}" alt="" /></div>` +
      `<div>` +
      `<h2 class="rc-title">${esc(NOTICE.title)}</h2>` +
      (reportTitle ? `<p class="rc-sub">${esc(reportTitle)}</p>` : "") +
      `<label class="rc-assent" for="rc-assent"><input type="checkbox" id="rc-assent" />` +
      `<span>${esc(NOTICE.assent)}</span></label></div>`,
    showCancelButton: true,
    reverseButtons: true,
    focusConfirm: false,
    buttonsStyling: false,
    confirmButtonText: NOTICE.confirm[act],
    cancelButtonText: NOTICE.cancel,
    allowOutsideClick: () => !busy,
    allowEscapeKey: () => !busy,
    customClass: {
      popup: "rc-popup",
      htmlContainer: "rc-body",
      actions: "rc-actions",
      confirmButton: "rc-confirm",
      cancelButton: "rc-cancel",
    },
    didOpen: () => {
      // the header image must sit flush with the popup edge, so lift it out of the padded body
      const body = Swal.getHtmlContainer();
      const head = body.querySelector(".rc-head");
      if (head) Swal.getPopup().insertBefore(head, Swal.getPopup().firstChild);

      const box = body.querySelector("#rc-assent");
      const confirm = Swal.getConfirmButton();
      confirm.disabled = true;
      box.addEventListener("change", () => { if (!busy) confirm.disabled = !box.checked; });
      box.addEventListener("keydown", (e) => {            // Space ticks, Enter confirms
        if (e.key === "Enter" && box.checked && !busy) { e.preventDefault(); confirm.click(); }
      });
      setTimeout(() => box.focus(), 50);
    },
    preConfirm: async () => {
      const confirm = Swal.getConfirmButton();
      const label = confirm.textContent;
      busy = true;
      confirm.disabled = true;
      Swal.getCancelButton().disabled = true;
      confirm.innerHTML = `<span class="rc-spin" aria-hidden="true"></span><span>${esc(NOTICE.preparing[act])}</span>`;
      try {
        const res = await api.post("/report-custody-ack", {
          survey_assignment_id: surveyAssignmentId,
          report_template_id: reportTemplateId,
          action: act,
          notice_version: NOTICE_VERSION,
        });
        return res.data;
      } catch (err) {
        // Stay open and re-enable: nothing is released without a recorded agreement
        busy = false;
        confirm.textContent = label;
        confirm.disabled = false;
        Swal.getCancelButton().disabled = false;
        Swal.showValidationMessage(NOTICE.error);
        return false;
      }
    },
  });
  return result.isConfirmed ? result.value : null;
}

// ---------------------------------------------------------------------------
// Iframe hooks: lets the parent app (which holds the real session) guard report
// pages WITHOUT editing the ~100 report HTML files. Requires same-origin iframe.
// ---------------------------------------------------------------------------
function installPrintFooter(doc, ack) {
  let el = doc.getElementById("custody-print-footer");
  if (!el) {
    const style = doc.createElement("style");
    style.textContent =
      "#custody-print-footer{display:none}" +
      "@media print{#custody-print-footer{display:block;position:fixed;left:0;right:0;bottom:0;" +
      "padding:4px 12px;font:10px/1.2 Arial,sans-serif;color:#000;background:#fff;text-align:center;" +
      "border-top:1px solid #999;z-index:2147483647}}";
    doc.head.appendChild(style);
    el = doc.createElement("div");
    el.id = "custody-print-footer";
    doc.body.appendChild(el);
  }
  const when = String(ack.accepted_at || "").replace("T", " ").replace("Z", " UTC");
  el.textContent = `Confidential - Personal Data. Printed by ${ack.viewer_name} | ${when} | Ref ${String(ack.ack_id).slice(0, 8)}`;
}

function installAxiosInterceptor(win) {
  const ax = win.axios;
  if (!ax || !ax.interceptors || win.__custodyAxios) return;
  win.__custodyAxios = true;
  const isPdf = (u) => typeof u === "string" && u.indexOf("/get-pdf-content") !== -1;
  ax.interceptors.request.use((config) => {
    try {
      if (isPdf(config.url) && win.__custodyPending) {
        config.data = Object.assign({}, config.data, { ack_id: win.__custodyPending.ack_id });
      }
    } catch (_) { /* never break the report's own request */ }
    return config;
  });
  ax.interceptors.response.use((resp) => {
    if (resp && resp.config && isPdf(resp.config.url)) win.__custodyPending = null; // consumed
    return resp;
  });
}

export function hookReportIframe(iframe, { api, surveyAssignmentId, reportTemplateId, reportTitle }) {
  let win, doc;
  try {
    win = iframe.contentWindow;
    doc = iframe.contentDocument;
    void doc.body; // throws if cross-origin
  } catch (e) {
    console.warn("[custody] report iframe is not same-origin; hooks not installed");
    return false;
  }
  if (!win || !doc || win.__custodyHooked) return !!win;
  win.__custodyHooked = true;

  const ctx = { api, surveyAssignmentId, reportTemplateId, reportTitle };

  // 1) "Access PDF" (#generatePdfBtn) - capture phase on the window so it runs before the
  //    report's own handlers and survives the report rewriting document.body.innerHTML.
  win.addEventListener(
    "click",
    async (e) => {
      const btn = e.target && e.target.closest ? e.target.closest("#generatePdfBtn") : null;
      if (!btn || win.__custodyPass) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      if (win.__custodyBusy) return;
      win.__custodyBusy = true;
      try {
        const ack = await requestAcknowledgement({ ...ctx, action: "pdf_download" });
        if (!ack) return;
        win.__custodyPending = ack;
        win.__custodyPass = true;
        btn.click(); // re-fire; our own listener lets this one through
        win.__custodyPass = false;
      } finally {
        win.__custodyBusy = false;
      }
    },
    true
  );
  installAxiosInterceptor(win);

  // 2) "Download Report PDF" (inline onclick="printPDF()") - the inline handler looks the
  //    function up at click time, so replacing the global is enough.
  if (typeof win.printPDF === "function" && !win.printPDF.__custody) {
    const original = win.printPDF;
    const wrapped = async function () {
      if (win.__custodyBusy) return;
      win.__custodyBusy = true;
      try {
        const ack = await requestAcknowledgement({ ...ctx, action: "print" });
        if (!ack) return;
        installPrintFooter(doc, ack);
        original.apply(win, arguments);
      } finally {
        win.__custodyBusy = false;
      }
    };
    wrapped.__custody = true;
    win.printPDF = wrapped;
  }
  return true;
}
